import { api } from '@/lib/api';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosProgressEvent, AxiosResponse } from 'axios';
import {
  BuildVideoEnhancementsResponse,
  FetchMediaParams,
  FetchMediaResponse,
  MediaItem,
  MediaListParams,
  MediaListResponse,
  UploadMediaFormData,
  UploadMediaResponse,
} from './types';

// EXISTING FUNCTION - Updated to support new parameters
const _fetchMedia = async ({
  accountNumber,
  title,
  pageSize,
  nextPageUrl,
}: FetchMediaParams): Promise<AxiosResponse<FetchMediaResponse>> => {
  if (nextPageUrl) {
    return api.get(nextPageUrl.replace('http://', 'https://'));
  }
  return api.get(`v2/media?account_id=${accountNumber}&title=${title}&limit=${pageSize}`);
};

// NEW FUNCTION - Extended version with full parameter support
const _fetchMediaList = async (
  params: MediaListParams
): Promise<AxiosResponse<MediaListResponse>> => {
  const searchParams = new URLSearchParams({
    account_id: params.account_id,
    limit: params.limit?.toString() || '25',
    ordering: params.ordering || '-created_date',
  });

  if (params.offset) searchParams.set('offset', params.offset.toString());
  if (params.query) searchParams.set('query', params.query);
  if (params.identity_id) searchParams.set('identity_id', params.identity_id);
  if (params.media_type) searchParams.set('media_type', params.media_type);

  return api.get(`v2/media?${searchParams.toString()}`);
};

const useFetchMedia = (
  accountNumber?: FetchMediaParams['accountNumber'],
  title?: FetchMediaParams['title'],
  pageSize?: FetchMediaParams['pageSize'],
  enabled?: boolean
) => {
  return useInfiniteQuery<AxiosResponse<FetchMediaResponse>, Error>({
    queryKey: ['fetch-media', { accountNumber, title, pageSize }] as const, // Mark queryKey as readonly
    queryFn: (
      // Default pageParam is needed
      { pageParam = null }
    ) => {
      return _fetchMedia({
        accountNumber,
        title,
        pageSize,
        nextPageUrl: pageParam as string,
      });
    },
    enabled,
    staleTime: 0, // Always refetch for polling
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.data.next ? lastPage.data.next : undefined),
    refetchInterval: 10000,
  });
};

const _uploadMedia = async (
  formData: UploadMediaFormData,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<AxiosResponse<UploadMediaResponse>> => {
  return api.post('v2/media', formData, {
    headers: { Accept: 'application/json' },
    onUploadProgress,
  });
};

const useUploadMedia = () => {
  return useMutation({
    mutationFn: ({
      formData,
      onUploadProgress,
    }: {
      formData: UploadMediaFormData;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    }) => _uploadMedia(formData, onUploadProgress),
  });
};

// Build Video Enhancements API
const _buildVideoEnhancements = async (
  formData: UploadMediaFormData,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<AxiosResponse<BuildVideoEnhancementsResponse>> => {
  return api.post('v2/media/buildVideoEnhancements', formData, {
    headers: { Accept: 'application/json' },
    onUploadProgress,
  });
};

const useBuildVideoEnhancements = () => {
  return useMutation({
    mutationFn: ({
      formData,
      onUploadProgress,
    }: {
      formData: UploadMediaFormData;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    }) => _buildVideoEnhancements(formData, onUploadProgress),
  });
};

// NEW HOOK - Extended media list with full parameter support
const useFetchMediaList = (params: MediaListParams, enabled: boolean = true) => {
  return useInfiniteQuery<AxiosResponse<MediaListResponse>, Error>({
    queryKey: ['fetch-media-list', params] as const,
    queryFn: ({ pageParam = 0 }) =>
      _fetchMediaList({
        ...params,
        offset: pageParam as number,
      }),
    enabled,
    staleTime: 0, // Keep fresh for optimistic updates
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage = !!lastPage.data.next;
      return hasNextPage ? allPages.length * (params.limit || 25) : undefined;
    },
  });
};

// NEW FUNCTION - Delete media
const _deleteMedia = async (mediaId: string): Promise<void> => {
  await api.delete(`v2/media/${mediaId}`);
};

const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _deleteMedia,
    onMutate: async (mediaId: string) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['fetch-media-list'] });

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: ['fetch-media-list'],
      });

      // Optimistically remove the media item from all relevant queries
      queryClient.setQueriesData({ queryKey: ['fetch-media-list'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;

        const queryData = old as {
          pages: Array<{
            data: MediaListResponse;
          }>;
        };

        return {
          ...queryData,
          pages: queryData.pages.map((page) => ({
            ...page,
            data: {
              ...page.data,
              results: page.data.results.filter(
                (item: MediaItem) => item.media_id !== mediaId
              ),
            },
          })),
        };
      });

      // Return a context object with the snapshotted value
      return { previousQueries };
    },
    onError: (error, mediaId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['fetch-media-list'],
        exact: false,
      });
    },
  });
};

// NEW FUNCTION - Fetch individual media details (for polling)
const _fetchMediaDetails = async (mediaId: string): Promise<AxiosResponse<MediaItem>> => {
  return api.get(`v2/media/${mediaId}`);
};

const useFetchMediaDetails = (mediaId: string, enabled: boolean = true) => {
  return useQuery<AxiosResponse<MediaItem>, Error>({
    queryKey: ['media-details', mediaId] as const,
    queryFn: () => _fetchMediaDetails(mediaId),
    enabled: enabled && !!mediaId,
    refetchInterval: 10000, // Poll every 10 seconds
    staleTime: 0, // Always refetch for polling
  });
};

export {
  _fetchMediaDetails, // Export for custom polling hooks
  useBuildVideoEnhancements,
  useDeleteMedia,
  useFetchMedia,
  useFetchMediaDetails,
  useFetchMediaList,
  useUploadMedia,
};
